import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';

import { isArray, isEmpty, assign } from 'lodash';
import { Form as FormUnform } from '@unform/web';
import PropTypes from 'prop-types';
import { format, parse, subDays, addDays } from 'date-fns';
import * as Yup from 'yup';

import api from '~/services/api';
import { Autocomplete, DateTime } from '~/components/Form';
import { Select } from '~/components/Form/v2';
import { t } from '~/helpers';

import * as S from './styles';

export default function Form({ sending, submiting }) {
  const formRef = useRef(null);

  const [start, setStart] = useState(format(new Date(), 'dd/MM/yyyy HH:mm:ss'));
  const [tank, setTank] = useState({});
  const [tanque, setTanque] = useState({});
  const [shift, setShift] = useState({ initial: [], final: [] });

  const handleSubmit = useCallback(async (data) => {
    try {
      const schema = Yup.object().shape({
        codPosto: Yup.string()
          .typeError('Campo obrigatório')
          .required('Campo obrigatório'),
        mesano_i: Yup.string()
          .typeError('Campo obrigatório')
          .required('Campo obrigatório'),
        mesano_f: Yup.string()
          .typeError('Campo obrigatório')
          .required('Campo obrigatório'),
        codTanque: Yup.string()
          .typeError('Campo obrigatório')
          .required('Campo obrigatório'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const formData = assign(data, {
        dataIni: data.dataIni || data.mesano_i,
        dataFim: data.dataFim || data.mesano_f,
      });

      delete formData.mesano_i;
      delete formData.mesano_f;

      submiting(formData);
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        formRef.current.setErrors(validationErrors);
      }
    }
  }, []);

  const handleGetShift = useCallback(
    async (value, position) => {
      try {
        const URL = position === 'initial' ? 'getturnosinicio' : 'getturnosfim';
        const query =
          position === 'initial'
            ? { datahorainicio: value }
            : { datahorafinal: value };

        const { data } = await api.get(`/turno/${URL}`, {
          params: {
            ...query,
            codigoposto: tank.post_cod_posto,
            pesquisalista: true,
          },
        });

        setShift((state) => ({
          ...state,
          [position]: data.map((item) => ({
            ...item,
            label: `${item.turn_num_turn} - ${
              position === 'initial' ? item.hora_ini : item.hora_fim
            }`,
            value:
              position === 'initial'
                ? item.turn_datahora_ini
                : item.turn_datahora_fim,
          })),
        }));
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [tank]
  );

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  return (
    <FormUnform ref={formRef} onSubmit={handleSubmit}>
      <S.Wrapper>
        <Autocomplete
          name="codPosto"
          label="post_desc"
          url="usuarioposto"
          unique="post_cod_posto"
          title="Posto"
          getValue={(value) => {
            setTank(value);
            setTanque({ tafi_desc: '', tafi_cod_tafi: '' });

            formRef.current.setFieldValue('dataIni', '');
            formRef.current.setFieldValue('dataFim', '');
            formRef.current.setFieldValue('codTanque', '');
          }}
        />
        <S.Columns grid={2}>
          <DateTime
            time
            disableFuture
            name="mesano_i"
            label="Data/Hora Inicial"
            getValue={(value) => {
              setStart(value);
              handleGetShift(value, 'initial');
              formRef.current.setFieldValue('dataIni', '');
            }}
          />

          <Select name="dataIni" label="Turno" data={shift.initial} />
        </S.Columns>
        <S.Columns grid={2}>
          <DateTime
            time
            disableFuture
            name="mesano_f"
            label="Data/Hora Final"
            minDate={format(
              subDays(parse(start, 'dd/MM/yyyy HH:mm:ss', new Date()), 30),
              'yyyy-MM-dd'
            )}
            maxDate={format(
              addDays(parse(start, 'dd/MM/yyyy HH:mm:ss', new Date()), 30),
              'yyyy-MM-dd'
            )}
            getValue={(value) => {
              handleGetShift(value, 'final');
              formRef.current.setFieldValue('dataFim', '');
            }}
          />

          <Select name="dataFim" label="Turno" data={shift.final} />
        </S.Columns>
        <Autocomplete
          disableClearable
          name="codTanque"
          label="tafi_desc"
          url="tanquefisico"
          unique="tafi_cod_tafi"
          title="Tanque"
          disabled={!tank.post_cod_posto}
          getValue={(value) => setTanque(value)}
          inputValue={tanque.tafi_desc || ''}
          params={{
            wherefield: ['post_cod_posto'],
            valuewherefield: [tank.post_cod_posto || 0],
            typewherefield: ['EQUALS'],
          }}
        />
      </S.Wrapper>
    </FormUnform>
  );
}

Form.propTypes = {
  sending: PropTypes.bool.isRequired,
  submiting: PropTypes.func.isRequired,
};
